import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Filter, Download, RefreshCw, Ticket } from 'lucide-react';

import TicketList from '@components/tickets/TicketList';
import TicketFilters from '@components/tickets/TicketFilters';
import Button from '@components/common/Button';
import { useAuth } from '@context/AuthContext';
import { ticketService } from '@services/ticketService';
import toast from 'react-hot-toast';

const TicketsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const { user, hasPermission } = useAuth();

  useEffect(() => {
    document.title = 'Tickets - RDX Ticketing System';
  }, []);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Build export parameters from URL search params
      const exportParams = {};
      
      // Get all search params and add them to export
      for (const [key, value] of searchParams.entries()) {
        exportParams[key] = value;
      }
      
      const response = await ticketService.exportTickets(exportParams, 'csv');
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tickets_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Tickets exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(error.response?.data?.message || 'Failed to export tickets');
    } finally {
      setIsExporting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.32,
        staggerChildren: 0.08
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32 } }
  };

  // Inline style objects to match Dashboard visual language
  const styles = {
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem'
    },
    hero: {
      borderRadius: '1rem',
      padding: '1.25rem',
      color: 'white',
      overflow: 'hidden',
      boxShadow: '0 20px 40px rgba(2,6,23,0.08)'
    },
    heroInner: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem'
    },
    heroLeft: {
      flex: 1,
      minWidth: 0
    },
    heroTitle: {
      fontSize: '1.375rem',
      fontWeight: 700,
      margin: 0,
      color: 'white',
      lineHeight: 1.1
    },
    heroSub: {
      marginTop: '0.375rem',
      fontSize: '0.9375rem',
      color: 'rgba(255,255,255,0.9)'
    },
    heroRight: {
      textAlign: 'right',
      minWidth: '11rem'
    },
    dateText: {
      margin: 0,
      fontSize: '0.9375rem',
      color: 'rgba(255,255,255,0.9)'
    },
    metaText: {
      marginTop: '0.25rem',
      fontSize: '0.8125rem',
      color: 'rgba(255,255,255,0.85)',
      textTransform: 'capitalize'
    },
    cardWrapper: {
      backgroundColor: 'transparent'
    },
    actionButtons: {
      display: 'flex',
      gap: '0.5rem',
      marginTop: '0.5rem'
    }
  };

  // gradient background for tickets (blue theme)
  const heroGradient = {
    background: 'linear-gradient(90deg, var(--primary-600), var(--primary-700))'
  };

  return (
    <motion.div
      style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Tickets Hero */}
      <motion.div variants={itemVariants}>
        <div style={{ ...styles.hero, ...heroGradient }}>
          <div style={styles.heroInner}>
            <div style={styles.heroLeft}>
              <h1 style={styles.heroTitle}>
                <Ticket style={{ display: 'inline-block', width: '1.5rem', height: '1.5rem', marginRight: '0.5rem' }} />
                Support Tickets
              </h1>
              <p style={styles.heroSub}>
                Manage and track all customer support tickets across your organization.
              </p>
            </div>

            <div style={styles.heroRight} className="hidden-md">
              <div style={styles.actionButtons}>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<RefreshCw style={{ width: 14, height: 14 }} />}
                  onClick={handleRefresh}
                >
                  Refresh
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Filter style={{ width: 14, height: 14 }} />}
                  onClick={() => setShowFilters(prev => !prev)}
                >
                  Filters
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Download style={{ width: 14, height: 14 }} />}
                  onClick={handleExport}
                  loading={isExporting}
                  disabled={isExporting}
                >
                  {isExporting ? 'Exporting...' : 'Export'}
                </Button>
                {hasPermission('can_create') && (
                  <Link to="/tickets/create">
                    <Button variant="primary" size="sm" leftIcon={<Plus style={{ width: 14, height: 14 }} />}>
                      New Ticket
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Filters */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            key="filters"
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, y: -10, transition: { duration: 0.2 } }}
          >
            <TicketFilters
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ticket List */}
      <motion.div variants={itemVariants}>
        <div style={styles.cardWrapper}>
          <TicketList searchParams={searchParams} refreshKey={refreshKey} />
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TicketsPage;
